import { create } from 'zustand';

export type ToastType = 'success' | 'error';

export interface ToastMessage {
  id: string;
  type: ToastType;
  title: string;
  message: string;
  duration: number;
}

interface ShowToastOptions {
  title: string;
  message: string;
  duration?: number;
}

interface ToastState {
  toast: ToastMessage | null;
  showSuccessMessage: (options: ShowToastOptions) => void;
  showErrorMessage: (options: ShowToastOptions) => void;
  dismissMessage: () => void;
}

const DEFAULT_DURATION = 5000;

let dismissTimer: ReturnType<typeof setTimeout> | null = null;

const clearDismissTimer = () => {
  if (dismissTimer) {
    clearTimeout(dismissTimer);
    dismissTimer = null;
  }
};

const scheduleDismiss = (dismissMessage: () => void, duration: number) => {
  clearDismissTimer();
  dismissTimer = setTimeout(() => {
    dismissMessage();
    dismissTimer = null;
  }, duration);
};

const showToast = (
  set: (partial: Partial<ToastState>) => void,
  get: () => ToastState,
  type: ToastType,
  options: ShowToastOptions
) => {
  const duration = options.duration ?? DEFAULT_DURATION;
  const toast: ToastMessage = {
    id: `${Date.now()}-${Math.random()}`,
    type,
    title: options.title,
    message: options.message,
    duration,
  };

  set({ toast });
  scheduleDismiss(get().dismissMessage, duration);
};

export const useToastStore = create<ToastState>((set, get) => ({
  toast: null,

  dismissMessage: () => {
    clearDismissTimer();
    set({ toast: null });
  },

  showSuccessMessage: (options) => {
    showToast(set, get, 'success', options);
  },

  showErrorMessage: (options) => {
    showToast(set, get, 'error', options);
  },
}));
